<template>
  <TwModal
    id="modal-user-map"
    :show="data.shouldShowLocationModal"
    v-on:close="closeModal"
  >
    <div class="tw-bg-white tw-rounded-xl tw-w-full md:tw-w-[520px]">
      <!-- MODAL FIRST PAGE : START -->
      <template v-if="!data.shouldShowLocationSearch">
        <div
          class="tw-w-full tw-text-center tw-p-6 tw-border-b tw-border-b-gray-300"
        >
          <p class="tw-text-gray-900 tw-text-lg tw-font-semibold">
            {{ __("searchbar.search_title") }}
          </p>
        </div>
        <div class="tw-px-6 tw-py-3">
          <div
            class="tw-mb-6"
            v-show="data.searchAddress"
          >
            <p class="tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-1.5">
              {{ __("searchbar.search_by") }}
            </p>
            <button
              type="button"
              @click="triggerSearchResults(true, true)"
              class="tw-flex tw-items-center tw-gap-x-2 tw-w-full placeholder:tw-text-gray-500 tw-py-2.5 tw-px-3.5 tw-rounded-lg tw-border tw-border-gray-300 tw-shadow-sm tw-text-gray-500 focus-ring-gray"
            >
              <span class="material-symbols-outlined tw-text-xl tw-leading-none"
                >search</span
              >
              <span>{{ __("searchbar.address_city_province") }}</span>
            </button>
          </div>
          <template v-if="data.locations?.length">
            <!-- CURRENT LOCATION BUTTON : START -->
            <div v-if="!data.locationBlocked">
              <button
                v-if="data.currentLocation"
                class="tw-w-full tw-bg-gray-50 tw-px-3 tw-py-2.5 tw-flex tw-gap-x-3 tw-items-center tw-text-gray-900 tw-font-semibold tw-rounded-lg tw-mt-3 focus-ring-gray"
                type="button"
                @click="selectLocation(data.currentLocation)"
              >
                <span class="material-icons-outlined tw-text-xl tw-leading-none"
                  >near_me</span
                >
                <span>{{ __("searchbar.current_location_text") }}</span>
              </button>
            </div>
            <!-- CURRENT LOCATION BUTTON : END -->

            <!-- LOCATIONS : START -->
            <div
              v-for="(location, index) in locationsSorted"
              class="tw-w-full tw-bg-gray-50 tw-px-3 tw-py-2.5 tw-flex tw-items-center tw-mt-3 tw-rounded-lg"
              :key="index"
            >
              <button
                type="button"
                class="tw-flex-grow tw-flex tw-gap-x-3 tw-items-center tw-text-gray-900 tw-font-semibold focus:tw-outline focus:tw-outline-gray-800"
                @click="selectLocation(location, false, true)"
              >
                <span
                  class="material-symbols-outlined tw-text-xl tw-leading-none"
                >
                  {{
                    location.label === "Home"
                      ? "home"
                      : location.label === "Work"
                      ? "work"
                      : "distance"
                  }}
                </span>
                <span>
                  {{
                    ["Home", "Work"].includes(location.label)
                      ? __("searchbar." + location.label)
                      : location.label
                  }}
                </span>
              </button>
              <span
                v-if="location.primary"
                class="tw-ml-auto tw-text-sm tw-text-primary-700"
                >{{ __("searchbar.default") }}</span
              >
              <button
                type="button"
                class="tw-ml-2 tw-bg-gray-100 tw-rounded-2xl tw-text-xs tw-font-medium tw-text-gray-700 tw-py-0.5 tw-px-2 focus:tw-outline focus:tw-outline-gray-800"
                @click="redirectToSettings"
              >
                {{ __("searchbar.edit_button_text") }}
              </button>
            </div>
            <!-- LOCATIONS : END -->
            <!-- RECENT LOCATIONS DROPDOWN : START -->
            <div
              v-if="recentLocation"
              class="tw-w-full tw-bg-gray-50 tw-px-3 tw-py-2.5 tw-flex tw-items-center tw-mt-3 tw-rounded-lg"
            >
              <button
                type="button"
                class="tw-flex-grow tw-flex tw-gap-x-3 tw-items-center tw-text-gray-900 tw-font-semibold focus:tw-outline focus:tw-outline-gray-800"
                @click="selectLocation(recentLocation, false, true)"
              >
                <span
                  class="material-symbols-outlined tw-text-xl tw-leading-none"
                  >schedule</span
                >
                <span>{{ recentLocation.label }}</span>
              </button>
            </div>
            <!-- RECENT LOCATIONS DROPDOWN : END -->
          </template>
        </div>
      </template>
      <!-- MODAL FIRST PAGE : END -->

      <!-- MODAL SECOND PAGE : START -->
      <template v-else>
        <div
          class="tw-w-full tw-text-center tw-p-6 tw-relative tw-border-b tw-border-b-gray-300"
        >
          <button
            class="material-symbols-outlined tw-leading-none tw-text-gray-900 tw-font-semibold tw-absolute tw-top-1/2 tw-left-6 -tw-translate-y-1/2 focus-ring-gray"
            @click="triggerSearchResults(false)"
          >
            arrow_back
          </button>
          <p class="tw-text-gray-900 tw-text-lg tw-font-semibold">
            {{ __("searchbar.search_title") }}
          </p>
        </div>
        <div class="tw-px-6 tw-py-3">
          <p class="tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-1.5">
            {{ __("searchbar.search_by") }}
          </p>
          <div
            id="perkLocationMap"
            :class="[
              loadingMap ? 'tw-opacity-30' : 'tw-opacity-100',
              'tw-mb-6',
            ]"
          >
            <MapComponent
              id-suffix="modal"
              :radius="parseInt(data.perkSearchDistance)"
              :initial-coordinates="data.initialCoords"
              :initial-address="data.autocompleteAddress"
              @location-did-change="locationDidChange"
              :show-map="true"
              :show-controls="false"
              :business="true"
              :placeholder="__('searchbar.search_perks_near')"
              :hide-ui="true"
              :should-focus="true"
            />
          </div>
          <p
            v-if="data.perkCount > 0"
            class="tw-text-xs tw-text-gray-600 tw-mb-1.5"
          >
            {{ data.perkCount }} {{ __("searchbar.perks_found_within") }}
            {{ data.perkSearchDistance }}km(s)
          </p>
          <!-- RADIUS SLIDER : START -->
          <div
            class="tw-flex tw-justify-between tw-items-center tw-gap-x-3 lg:tw-gap-x-6 tw-mb-6 distance-input"
          >
            <label
              class="tw-text-gray-700 tw-text-sm tw-font-medium"
              for="radius"
              >{{ __("searchbar.distance_text") }}</label
            >
            <input
              class="tw-flex-grow focus-ring-gray"
              ref="rangeInput"
              type="range"
              name="radius"
              id="radius"
              min="1"
              max="100"
              data-show-value="true"
              v-model="data.perkSearchDistance"
              @change="radiusWasChanged"
            />
            <p class="tw-text-sm tw-text-gray-700">
              {{ `${data.perkSearchDistance}km(s)` }}
            </p>
          </div>
          <!-- RADIUS SLIDER : END -->
          <div class="tw-flex tw-flex-col tw-gap-y-1.5">
            <label
              class="tw-text-sm tw-font-medium tw-text-gray-700"
              for="locationLabel"
              >{{ __("searchbar.label_text") }}</label
            >
            <div class="tw-flex tw-flex-col tw-gap-y-1.5">
              <input
                type="text"
                id="locationLabel"
                v-model="locationLabel"
                placeholder="Location Name"
                :class="[
                  'tw-rounded-lg tw-border tw-shadow-sm tw-py-2.5 tw-px-3.5 tw-text-gray-900 placeholder:tw-text-gray-500',
                  locationLabelError
                    ? 'tw-border-red-300'
                    : 'tw-border-gray-300',
                ]"
              />
              <span
                v-cloak
                v-if="locationLabelError"
                class="tw-text-sm tw-font-medium tw-text-red-600"
              >
                {{ locationLabelError }}
              </span>
            </div>
          </div>
        </div>
        <div
          class="tw-pt-8 tw-px-6 tw-pb-6 tw-border-t tw-border-t-gray-300 tw-flex tw-gap-x-3 tw-w-full"
        >
          <button
            type="button"
            class="tw-text-gray-700 tw-bg-white tw-font-semibold tw-py-2.5 tw-w-full tw-border tw-border-gray-300 tw-rounded-lg tw-shadow-sm disabled:tw-text-gray-400 disabled:tw-cursor-not-allowed"
            :disabled="data.selectedLocation == null || data.saving"
            @click="saveLocationSelection(false)"
          >
            {{ __("searchbar.apply_save_button_text") }}
          </button>
          <button
            type="button"
            class="tw-text-white tw-bg-primary-600 tw-font-semibold tw-py-2.5 tw-w-full tw-rounded-lg tw-shadow-sm disabled:tw-bg-primary-200 disabled:tw-cursor-not-allowed"
            :disabled="data.selectedLocation == null || data.saving"
            @click="saveLocationSelection()"
          >
            {{ __("searchbar.apply_button_text") }}
          </button>
        </div>
      </template>
    </div>
  </TwModal>
</template>

<script setup lang="ts">
import {
  reactive,
  computed,
  watch,
  ref,
  onMounted,
  getCurrentInstance,
} from "vue";
import { useField, useForm } from "vee-validate";
import * as yup from "yup";
import Cookies from "js-cookie";
import TwModal from "components/TwModal.vue";
import MapComponent from "components/MapComponent.vue";
import User from "lib/User";
import createSearchServiceWithOptions from "lib/workperks/services/createSearchServiceWithOptions";
import ESSearchService from "lib/workperks/services/ESSearchService";
import { axios } from "lib/Init";
import { getBrowserLocation } from "lib/Utilities";
import { SweetAlertTailwind as Swal } from "lib/SwalExtension";

const props = defineProps({
  primaryLocation: {
    required: true,
    type: Object,
  },
});

const instance = getCurrentInstance();
const __ = instance?.appContext.config.globalProperties.__;

const data = reactive({
  query: "",
  lastSelectedIndex: -1,
  showResults: false,
  focusResult: null,
  isLoading: false,
  shouldShowLocationModal: false,
  shouldShowLocationSearch: false,
  locations: [],
  selectedLocation: null,
  resultSize: 0,
  currentLocation: null,
  recentLocations: [],
  perkCount: 0,
  user: window.s.user,
  perkSearchDistance: "50",
  searchAddress: false,
  saving: false,
  locationBlocked: false,
  autocompleteAddress: "",
  initialCoords: {
    lat: 0,
    lng: 0,
  },
});

const mapComponent = ref(null);
const rangeInput = ref(null);

const schema = yup.object({
  locationLabel: yup
    .string()
    .required(__("searchbar.location_label_required")),
});

const { handleSubmit, resetForm } = useForm({
  validationSchema: schema,
});

const { value: locationLabel, errorMessage: locationLabelError } =
  useField("locationLabel");

let loadingMap;
let searchService;

const locationsSorted = computed(() => {
  const res = [];
  data.locations.forEach((loc) => {
    ["Home", "Work"].includes(loc.label) ? res.unshift(loc) : res.push(loc);
  });
  return res;
});

const recentLocation = computed(() => {
  return JSON.parse(localStorage.getItem("recentLocation")) || null;
});

// FIXME: Should be using the route instead of a static URL.
const closeModal = () => {
  data.shouldShowLocationModal = false;
  data.shouldShowLocationSearch = false;
};

const showLocationModal = () => {
  data.shouldShowLocationModal = true;
};

const redirectToSettings = () => {
  window.location.href = "/settings#Locations";
  closeModal();
};

// Executed when current location found
const setCoordinatesFromBrowser = (position) => {
  data.currentLocation = {
    latitude: position.coords.latitude,
    longitude: position.coords.longitude,
  };
  data.initialCoords.lat = position.coords.latitude;
  data.initialCoords.lng = position.coords.longitude;
  loadingMap = false;
  loadUserAddresses();
};

// Get all user addresses
const loadUserAddresses = () => {
  axios
    .get("/api/user/locations")
    .then((response) => {
      if (response.data.success) {
        data.searchAddress = true;
        data.locations = response.data.locations;
      }
    })
    .catch((error) => {
      console.error("API request failed:", error);
      data.searchAddress = false;
      data.locations = [];
    });
};

// Executed when No current Location found
const currentLocationNotFound = (error) => {
  loadingMap = false;
  loadUserAddresses();
  data.locationBlocked = !!error;
};

// location is changed on map or by search results
const locationDidChange = (location) => {
  // don't show current location if a user reside outside US and CA
  if (!["USA", "US", "CA"].includes(location.address.country)) {
    data.locationBlocked = true;
  }
  const street_number =
    location.address.street_number !== null
      ? `${location.address.street_number} `
      : "";
  const address = location.address.route
    ? street_number + location.address.route
    : null;
  const city = location.address.locality
    ? location.address.locality
    : location.address.administrative_area_level_3;

  const locationObject = {
    latitude: location.coordinates.lat,
    longitude: location.coordinates.lng,
    address1: address,
    city,
    country: location.address.country,
    province: location.address.administrative_area_level_1,
    postal_code: location.address.postal_code,
    range: location.address.range ?? data.perkSearchDistance,
  };

  if (
    !data.locationBlocked &&
    isCurrentLocation(location) &&
    mapComponent.value.autocompleteAddress === location.formattedAddress
  ) {
    data.currentLocation = {
      ...locationObject,
      tag: "currentLocation",
    };

    getPerkCountForLocation(data.currentLocation);
  } else {
    data.selectedLocation = locationObject;

    getPerkCountForLocation(data.selectedLocation);
  }
};

// User wants to start searching
const triggerSearchResults = (trigger, newSearch = false) => {
  data.shouldShowLocationSearch = trigger;

  if (trigger && newSearch) {
    data.saving = false;
  }

  // If Back button is hit, clear selectedLocation object and from Input Box
  if (!trigger || newSearch) {
    data.selectedLocation = null;
    resetForm();
  }
};

const updateLocalStorageDistance = (location) => {
  const filters = JSON.parse(localStorage.filters);
  filters.distance = location.range;
  localStorage.setItem("filters", JSON.stringify(filters));
};

const selectLocation = (
  location,
  showSearchResults = true,
  shouldSaveLocationSelection = false
) => {
  data.selectedLocation = location;
  data.autocompleteAddress = data.selectedLocation
    ? data.selectedLocation.address1
    : "";
  if (localStorage.filters) {
    updateLocalStorageDistance(location);
  }

  if (showSearchResults) {
    triggerSearchResults(true);
  }
  if (shouldSaveLocationSelection) {
    saveLocationSelection();
  }
};

const saveLocationSelection = (viewOnly = true) => {
  data.saving = true; // disable the save buttons

  if (
    !viewOnly &&
    data.selectedLocation &&
    !isLocationInArray(data.locations, data.selectedLocation)
  ) {
    handleSubmit(
      async (values) => {
        data.selectedLocation.label = values.locationLabel;
        try {
          const response = await axios.put(
            "/api/profile/location",
            data.selectedLocation
          );

          if (response.data.success) {
            storeCookie(false);
          } else if(response.data.location == false && response.data.success === false) {
        
            await Swal.fire({
              icon: "error",
              text: __("searchbar.location_label_exists"),
              allowOutsideClick: false,
            });
           
          } else {
              await Swal.fire({
                icon: "error",
                text: __("searchbar.location_save_error_text"),
                allowOutsideClick: false,
              });
          }
          data.shouldShowLocationModal = true;
          data.shouldShowLocationSearch = true;
          data.saving = false;
        } catch (error) {
            data.saving = false;
            if(error.response && error.response.data && error.response.data.message){
              await Swal.fire({
                icon: "error",
                text: error.response.data.message,
                allowOutsideClick: false,
              });
            } else {
              await Swal.fire({
                icon: "error",
                text: __("searchbar.location_save_error_text"),
                allowOutsideClick: false,
              });
            }
            data.shouldShowLocationModal = true;
            data.shouldShowLocationSearch = true;
        }
      },
      () => {
        data.saving = false;
      }
    )();
  } else if (
    data.selectedLocation &&
    !isLocationInArray(data.locations, data.selectedLocation)
  ) {
    data.selectedLocation.label = data.selectedLocation?.address1 || data.selectedLocation?.city || data.selectedLocation?.province ||data.selectedLocation?.country;
    // Call endpoint to update user license based on country
      axios.post("/api/profile/license", data.selectedLocation)
        .then(async (response) => {
          if (response.data.success) {
            localStorage.setItem("recentLocation", JSON.stringify(data.selectedLocation));
            if (localStorage.filters) {
              updateLocalStorageDistance(data.selectedLocation);
            }
            storeCookie(true);
          } else {
            const errorMessage = response.data?.message || __("searchbar.location_save_error_text");
            await Swal.fire("Error", errorMessage, "error");
            data.shouldShowLocationModal = true;
            data.shouldShowLocationSearch = true;
          }
        })
        .catch(async (error) => {
          const errorMessage = error.response?.data?.message || __("searchbar.location_save_error_text");
          await Swal.fire("Error", errorMessage, "error");
          data.shouldShowLocationModal = true;
          data.shouldShowLocationSearch = true;
        })
        .finally(() => {
          data.saving = false;
        });
    } else  {
      // Location already exists in user's locations, just store cookie and reload
      storeCookie(true);
      
    } 
};

const storeCookie = (viewOnly) => {
  if (!("label" in data.selectedLocation) && viewOnly) {
    // If apply is clicked the label is not required for storing in the database
    data.selectedLocation.label = data.selectedLocation.address1
      ? data.selectedLocation.address1
      : data.selectedLocation.city;
  }

  if (
    !isLocationInArray(getRecentLocationsFromCookie(), data.selectedLocation)
  ) {
    data.recentLocations.push(data.selectedLocation);
    if (data.recentLocations.length > 10) {
      data.recentLocations.shift(); // Remove oldest location (FIFO)
    }
  }

  setLocationCookie(window.s.user, data.selectedLocation, data.recentLocations);
  const checkPath = window.location.pathname.split("/")[1];
  // if we are on the perk detail page we need to redirect to home as the perk detail page breaks if location is switched between US and CA
  checkPath === "offer" ? (window.location.href = "/home") : location.reload();
};

const isLocationInArray = (locationArray, location) => {
  let match = 0;

  locationArray.forEach((element) => {
    if (
      (element.address1 ? element.address1 : null) === location.address1 &&
      element.city === location.city
    ) {
      match++;
    }
  });

  return match > 0;
};

const setLocationCookie = (user, location, recentLocations = []) => {
  location.range = location.range ?? 50;
  const userLocationCookie = {
    userId: user.id,
    savedPrimaryLocation: location,
    recentLocations,
  };
  let venngoCookies = Cookies.get("venngo_cookies");
  if (venngoCookies) {
    venngoCookies = JSON.parse(venngoCookies);
  } else {
    venngoCookies = {};
  }
  venngoCookies.locationState = userLocationCookie;
  Cookies.set("venngo_cookies", JSON.stringify(venngoCookies), {
    expires: 365,
  });
};

// Try to retrieve location from Cookies
const getRecentLocationsFromCookie = () => {
  if (Cookies.get("venngo_cookies")) {
    const venngoCookies = JSON.parse(Cookies.get("venngo_cookies"));
    if (venngoCookies.locationState) {
      const userLocationCookie = venngoCookies.locationState;
      if (userLocationCookie.userId !== window.s.user.id) {
        return [];
      }
      return userLocationCookie.recentLocations
        ? userLocationCookie.recentLocations
        : [];
    }
    return [];
  }
  return [];
};

const isCurrentLocation = (location) => {
  return (
    location.coordinates.lat.toFixed(3) ===
      data.currentLocation.latitude.toFixed(3) &&
    location.coordinates.lng.toFixed(3) ===
      data.currentLocation.longitude.toFixed(3)
  );
};

const getPerkCountForLocation = (location) => {
  searchService
    .setDistance(location.range)
    .setLocation(location)
    .setESSource(false)
    .search()
    .then((perks) => {
      // ES is asked for size 0 (see initSearchService), so count from hits.total.
      data.perkCount =
        searchService instanceof ESSearchService
          ? searchService.getTotalHits() ?? 0
          : perks?.length ?? 0;
    });
};

const initSearchService = () => {
  searchService = createSearchServiceWithOptions({
    user: User.getCurrent().data,
    location: User.getCurrent().primaryLocation,
    resultSize: 10000,
  });

  // PM-5223: the /es proxy rejects size > 200. Only the count is needed, which
  // ES returns in hits.total even with size 0.
  if (searchService instanceof ESSearchService) {
    searchService.setResultSize(0);
  }
};

const radiusWasChanged = () => {
  if (data.selectedLocation) {
    data.selectedLocation.range = parseInt(data.perkSearchDistance);
    getPerkCountForLocation(data.selectedLocation);
  }
};

watch(
  () => data.perkSearchDistance,
  (v) => {
    const fill = Number(v) ? String(v) : "50";
    rangeInput.value.style.backgroundSize = `${fill}% 100%`;
  }
);

watch(
  () => props.primaryLocation,
  (l) => {
    data.initialCoords.lat = l.latitude;
    data.initialCoords.lng = l.longitude;
  },
  {
    immediate: true,
  }
);

onMounted(() => {
  loadingMap = true;
  initSearchService();
  data.recentLocations = getRecentLocationsFromCookie();
  getBrowserLocation(setCoordinatesFromBrowser, currentLocationNotFound);
});

defineExpose({
  closeModal,
  showLocationModal,
  redirectToSettings,
  setCoordinatesFromBrowser,
  loadUserAddresses,
  currentLocationNotFound,
  locationDidChange,
  triggerSearchResults,
  updateLocalStorageDistance,
  selectLocation,
  saveLocationSelection,
  storeCookie,
  isLocationInArray,
  setLocationCookie,
  getRecentLocationsFromCookie,
  isCurrentLocation,
  getPerkCountForLocation,
  initSearchService,
  radiusWasChanged,
});
</script>
